import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { getTeamMembers as getStaticTeamMembers } from '../config/team';

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  imageUrl?: string;
  skills?: string[];
  experience?: string;
  email?: string;
  linkedin?: string;
  xing?: string;
  location?: string;
  joinDate?: string;
  achievements?: string[];
  createdAt?: string;
  updatedAt?: string;
}

// API response type with nested social object
interface ApiTeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  imageUrl?: string;
  skills?: string[];
  experience?: string;
  social?: {
    email?: string;
    linkedin?: string;
    xing?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface UseTeamMembersReturn {
  teamMembers: TeamMember[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Custom hook to fetch team members from the API with fallback to static config.
 * This allows the team section to render even when the backend is unavailable.
 * Always uses local static images as fallback if API images are not available.
 */
export function useTeamMembers(): UseTeamMembersReturn {
  const { lang } = useParams<{ lang?: string }>();
  const currentLang = lang || 'en';
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTeamMembers = async () => {
    // Get static members as fallback for images and data
    const staticMembers = getStaticTeamMembers(currentLang);
    
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/api/team-members?lang=${currentLang}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch team members: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Merge API data with static images as fallback
      const members: TeamMember[] = (data.teamMembers || []).map((member: ApiTeamMember) => {
        // Find corresponding static member for image fallback
        const staticMember = staticMembers.find(sm => sm.id === member.id);
        
        return {
          ...member,
          email: member.social?.email,
          linkedin: member.social?.linkedin,
          xing: member.social?.xing,
          // Use static image if API doesn't provide one or if it's a relative path
          imageUrl: (member.imageUrl && member.imageUrl.startsWith('http')) 
            ? member.imageUrl 
            : staticMember?.imageUrl || member.imageUrl
        };
      });
      
      setTeamMembers(members);
    } catch (err) {
      // Fallback to static config when backend is unavailable
      console.warn('Backend unavailable, falling back to static team config:', err);
      setTeamMembers(staticMembers as TeamMember[]);
      setError(null); // Don't show error when using fallback
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, [currentLang]);

  return {
    teamMembers,
    loading,
    error,
    refetch: fetchTeamMembers,
  };
}
